
import { IJourney } from "@interfaces/IJourney";
import { IPlot, IPlotEvent } from "@interfaces/IPlot";
import { EventType } from "@interfaces/ISensorData";
import { IVehicleDriverMapping } from "@interfaces/IVehicleDriverMapping";
import { IVehicleInput } from "@interfaces/IVehicleInput";
import { TranslationService } from "@services/translation.service";
import { CameraDisplayService } from "@videos/cameraDisplay.service";
import * as moment from "moment";
import { AccelerometerInfoType } from "../enum/AccelerometerInfoType";
import { ReasonCode } from "../enum/ReasonCode";
import { VideoStatus } from "../main/components/videos/enums/VideoStatus";
import { IVideoEntry } from "../main/components/videos/models/IVideoEntry";
import { DriverService } from "./driver.service";
import { GoogleMarkerService } from "./googleMarker.service";
import { PlotService } from "./plot.service";

export class PlotEventService {

    static $inject = ["googleMarkerService", "driverService", "cameraDisplayService", "plotService", "translationService"];

    constructor(
        private readonly googleMarkerService: GoogleMarkerService,
        private readonly driverService: DriverService,
        private readonly cameraDisplayService: CameraDisplayService,
        private readonly plotService: PlotService,
        private readonly translationService: TranslationService
    ) {
    }

    createJourneyStartStopEvent(plot: IPlot, label: "Start" | "Stop"): IPlotEvent {
        var anchorPosition = this.googleMarkerService.getEventMarkerAnchorPosition();
        return this.googleMarkerService.createEventMarker(
            plot,
            {
                label: this.translationService.t(`plotEventService.${label.toLowerCase()}`),// this will be start or stop, convert it with translation service
                markerClass: label == "Start" ? "marker-event-start" : "marker-event-end",
                subIcon: label == "Start" ? "play_arrow" : "stop",
                showLabel: true,
                anchorPosition: anchorPosition
            });
    }

    //createJourneyStartEvent(plot: IPlot, journey: IJourney): IPlotEvent {
    //    var marker = this.veriMarkerService.createEventMarker(plot, "Journey Start", "marker-event-end", "play_arrow", true);
    //    marker.journey = journey;
    //    return marker;
    //}

    //createJourneyEndEvent(plot: IPlot, journey: IJourney): IPlotEvent {
    //    var marker = this.veriMarkerService.createEventMarker(plot, "Journey End", "marker-event-end", "stop", true);
    //    marker.journey = journey;
    //    return marker;
    //}

    hasCANPTOEvents(plots: IPlot[]) {
        for (let i = 0; i < plots.length; i++) {
            const plot = plots[i];

            if (plot.Reason === ReasonCode.Fms && plot.SensorData) {
                const eventType = plot.SensorData.EventType;

                if (eventType === EventType.Pto) {
                    return true;
                }
            }
        }

        return false;
    }

    setJourneyEvents(events: IPlotEvent[], journey: IJourney): void {

        if (!events || !journey) {
            return;
        }

        journey.events = [];

        const startDateMoment = moment(journey.StartUtc);
        const endDateMoment = moment(journey.EndUtc);

        for (let i = 0; i < events.length; i++) {
            const event = events[i];

            const pointInRange = event.plot.UtcMoment && event.plot.UtcMoment.isBetween(startDateMoment, endDateMoment, null, "[]");

            if (pointInRange) {
                event.journey = journey;
                if (event.plot && !event.plot.color || !event.lineColor) {
                    event.plot.color = journey.lineColor;
                    event.lineColor = journey.lineColor;
                }

                event.options = event.buildOptions();

                journey.events.push(event);
            }
        }
    }

    parsePlotEvents(plots: IPlot[], driverAssignments: IVehicleDriverMapping[], vehicleInputs: IVehicleInput[], videos: IVideoEntry[], includeTelematicsPTO: boolean, startDate?: moment.Moment, endDate?: moment.Moment): IPlotEvent[] {

        const events: IPlotEvent[] = [];

        const journeyStartPlot = plots[0];
        const journeyEndPlot = plots[plots.length - 1];

        var adjustedStartDate = journeyStartPlot ? journeyStartPlot.UtcMoment : startDate || moment().startOf("day");
        var adjustedEndDate = journeyEndPlot ? journeyEndPlot.UtcMoment : endDate || moment().endOf("day");

        videos.forEach(videoEntry => {
            if (videoEntry.Status !== VideoStatus.Downloaded && moment().add(-7, "days").isAfter(videoEntry.EventTimeUtc)) {
                return true;
            }

            if (adjustedStartDate.isSameOrBefore(videoEntry.EventTimeUtc) && adjustedEndDate.isSameOrAfter(videoEntry.EventTimeUtc)) {
                if (videoEntry.Latitude === 0 && videoEntry.Longitude === 0) {
                    const matchingPlot = this.plotService.findMatchingPlot(moment(videoEntry.VideoStartTime), moment(videoEntry.VideoEndTime), plots);

                    if (matchingPlot) {
                        videoEntry.Latitude = matchingPlot.Latitude;
                        videoEntry.Longitude = matchingPlot.Longitude;
                        videoEntry.PlaceName = matchingPlot.LocationInformation?.LocationDescription;
                    }
                }

                const plotEvent = this.cameraDisplayService.createEventFromVideo(videoEntry);

                plotEvent.video = videoEntry;

                events.push(plotEvent);
            }
        });

        driverAssignments.forEach(assignment => {

            var label = "";
            var eventTime: moment.Moment;

            var driver = this.driverService.getCachedDriver(assignment.DriverId);
            if (driver) {

                // Assignment
                if (assignment.StartUtc.isBetween(adjustedStartDate, adjustedEndDate, null, "[]")) {
                    eventTime = assignment.StartUtc;
                    label = this.translationService.t('plotEventService.driverLogon' , driver.Name);
                } else if (assignment.EndUtc.isBetween(adjustedStartDate, adjustedEndDate, null, "[]")) {
                    eventTime = assignment.EndUtc;
                    label = this.translationService.t('plotEventService.driverLogoff', driver.Name);
                }

                if (label) {
                    // Find nearest plot to the assignment
                    var nearestPlot = journeyStartPlot;
                    for (var i = 0; i < plots.length; i++) {
                        const plot = plots[i];
                        if (plot.UtcMoment.isSameOrAfter(eventTime)) {
                            nearestPlot = plot;
                            break;
                        }
                    }

                    if (nearestPlot) {
                        events.push(this.googleMarkerService.createEventMarker(nearestPlot, {
                            label: label,
                            markerClass: "marker-event-driver-changed",
                            subIcon: "person"
                        }));
                    }

                }
            }
        });

        for (let i = 0; i < plots.length; i++) {
            const plot = plots[i];
            if (!plot) {
                continue;
            }

            if (adjustedStartDate && adjustedEndDate && !plot.UtcMoment.isBetween(adjustedStartDate, adjustedEndDate, null, "[]")) {
                continue;
            }

            //console.log(plot.UtcMoment.format(), "Plot.DriverID", plot.DriverId);

            //var anchorPosition = this.googleMarkerService.getEventMarkerPosition(plot, journeyStartPlot, journeyEndPlot);

            //if (plot.Reason !== ReasonCode.Fms) {
            //    this.speedData.push([plot.UtcMoment.valueOf(), this.kphToUserUnitService.convertUnit(plot.Velocity)]);
            //}

            if (plot.Reason === ReasonCode.IgnitionOn) {
                events.push(this.createJourneyStartStopEvent(plot, "Start"));
            }
            else if (plot.Reason === ReasonCode.IgnitionOff) {
                events.push(this.createJourneyStartStopEvent(plot, "Stop"));
            }
            else if (plot.Reason === ReasonCode.FmsOn) {
                //this.parseTimedCANEvent(plot);
                //events.push(this.veriMarkerService.createEventMarker(
                //    plot,
                //    "Engine On",
                //    "marker-event-pause",
                //    "settings_input_svideo"));
            }
            else if (plot.Reason === ReasonCode.FmsOff) {
                //events.push(this.veriMarkerService.createEventMarker(
                //    plot,
                //    "Engine Off",
                //    "marker-event-pause",
                //    "settings_input_svideo"));
            }
            else if (plot.Reason == ReasonCode.Accelerometer && plot.AccelerometerInfo) {
                var eventText = "";

                switch (plot.AccelerometerInfo.Direction) {
                    case AccelerometerInfoType.Acceleration:
                        eventText = this.translationService.t("plotEventService.acceleration");
                        break;

                    case AccelerometerInfoType.Deceleration:
                        eventText = this.translationService.t("plotEventService.deceleration");
                        break;

                    case AccelerometerInfoType.Cornering:
                        eventText = this.translationService.t("plotEventService.cornering");
                        break;
                }

                events.push(this.googleMarkerService.createEventMarker(
                    plot,
                    {
                        label: eventText,
                        markerClass: "marker-event-bad-anticipation",
                        subIcon: "local_car_wash"
                    }));
            }
            //else if (plot.Reason === ReasonCode.ThermoKing /*|| plot.Reason === ReasonCode.Reportz*/) {

            //    if (plot.Temperatures) {
            //        plot.Temperatures.forEach(temp => {
            //            var probePos = temp.Position - 1;

            //            if (!this.tempData[probePos]) {
            //                this.tempData[probePos] = [];
            //            }

            //            this.tempData[probePos].push([plot.UtcMoment.valueOf(), temp.Temperature]);
            //        });
            //    }
            //}
            else if (plot.Reason === ReasonCode.Fms && plot.SensorData) {
                //if (plot.CanParams.EventCode) {
                //} else {
                //    this.parseTimedCANEvent(plot, extensionArray);
                //}

                if (plot.SensorData.Header === "K0 03 041") {
                    //this.parseTimedCANEvent(plot);
                }
                else if (plot.SensorData.Header === "$EVT") {
                    const eventType = plot.SensorData.EventType;

                    //if (!eventArray[eventType]) {
                    //    eventArray[eventType] = 0;
                    //}

                    //eventArray[eventType]++;

                    if (eventType === EventType.OverRpm) { // RPM
                        //this.events.push(this.createEventMarker(
                        //    plot,
                        //    "RPMOn",
                        //    "marker-event-high-rpm",
                        //    "location_on",
                        //    "network_check"));
                    }
                    else if (eventType === EventType.OverTorque) { // Torque
                        //this.events.push(this.createEventMarker(
                        //    plot,
                        //    "TorqueOn",
                        //    "marker-event-high-torque",
                        //    "location_on",
                        //    "new_releases"));
                    }
                    else if (eventType === EventType.Pto) { // PTO
                        let plotEvent: IPlotEvent;

                        const ptoLabel = (plot.SensorData.EventFlag === 1) ? this.translationService.t("plotEventService.ptoOn") : this.translationService.t("plotEventService.ptoOff");
                        const ptoClass = (plot.SensorData.EventFlag === 1) ? "marker-event-pto" : "marker-event-pto pto-off";

                        plotEvent = this.googleMarkerService.createEventMarker(
                            plot,
                            {
                                label: ptoLabel,
                                markerClass: ptoClass,
                                subIcon: "build"
                            });

                        events.push(plotEvent);
                    }
                    else if (eventType === EventType.Idling) { // Idling
                        if (plot.SensorData.EventFlag === 1) {
                            events.push(this.googleMarkerService.createEventMarker(
                                plot,
                                {
                                    label: this.translationService.t("plotEventService.idling"),
                                    markerClass: "marker-event-idling",
                                    subIcon: "pause"
                                }));
                        }
                    }
                    //else if (eventType === Verilocation.App.PlotExtensionEventType.HarshBraking && extensionArray[2] !== "1") {
                    //}
                    else if (eventType === EventType.HarshBraking && plot.SensorData.EventFlag === 1) {
                        // Harsh brake
                        const plotEvent = this.googleMarkerService.createEventMarker(
                            plot,
                            {
                                label: this.translationService.t("plotEventService.harshBrake"),
                                markerClass: "marker-event-harsh-brake",
                                subIcon: "warning"
                            });

                        events.push(plotEvent);
                    }
                    else if (eventType === EventType.OverAcceleration && plot.SensorData.EventFlag === 1) { // over acceleration
                        //this.events.push(this.createEventMarker(
                        //    plot,
                        //    "OverAcceleration",
                        //    "marker-event-over-acceleration",
                        //    "location_on",
                        //    "fast_forward"));
                    }
                    else if (eventType === EventType.BadAnticipation) { // Bad anticipation
                        if (plot.SensorData.EventFlag === 1) {
                            const plotEvent = this.googleMarkerService.createEventMarker(
                                plot,
                                {
                                    label: this.translationService.t("plotEventService.badAnticipation"),
                                    markerClass: "marker-event-bad-anticipation",
                                    subIcon: "local_car_wash"
                                });

                            events.push(plotEvent);
                        }
                    }
                }
            }

            // Now removed, replaced with input triggers
            //if (includeTelematicsPTO && plot.Reason === ReasonCode.Telematics && plot.Telematics) {
            //    const input = this.vehicleInputService.getInputForVehicle(plot.VehicleId, vehicleInputs);
            //    if (input) {
            //        plot.Telematics.forEach(telematics => {
            //            const ptoLabel = (telematics.State === input.ActiveState) ? `${input.Name} On (Telematics)` : `${input.Name} Off (Telematics)`;
            //            const ptoClass = (telematics.State === input.ActiveState) ? "marker-event-pto" : "marker-event-pto pto-off";

            //            const plotEvent = this.googleMarkerService.createEventMarker(
            //                plot,
            //                ptoLabel,
            //                ptoClass,
            //                "build");

            //            events.push(plotEvent);
            //        });
            //    }
            //}
        }

        //console.timeEnd("parsePlotEvents");

        return events;
    }
}
